import { Box, Button, Dialog, TextField, Typography } from "@mui/material";
import { ArrowDropDown as ArrowDropDownIcon } from "@mui/icons-material";
import { ArrowDropUp as ArrowDropUpIcon } from "@mui/icons-material";
import { SetStateAction, useEffect, useState } from "react";
import { chargeOperationStyles } from "@/pages/sy/styles/ChargeOperationStyles";
import { useTheme } from "@mui/material/styles";
import { CustomKeyboard } from "@/pages/sy/components/CustomKeyboard";

export const CommentsModal = ({
  isLoading,
  keyboardVisible,
  setKeyboardVisible,
  commentInput,
  setCommentInput,
  isCommentModalOpen,
  handleCloseCommentModal,
  handleSaveComments,
}: {
  isLoading: boolean;
  keyboardVisible: boolean;
  setKeyboardVisible: (keyboardVisible: boolean) => void;
  commentInput: string;
  setCommentInput: (keyboardInput: SetStateAction<string>) => void;
  isCommentModalOpen: boolean;
  handleCloseCommentModal: () => void;
  handleSaveComments: (comments: string) => void;
}) => {
  const theme = useTheme();
  const styles = chargeOperationStyles(theme);

  const [layoutName, setLayoutName] = useState<"default" | "shift" | "numbers">(
    "default",
  );

  const onKeyPress = (button: string) => {
    if (button === "{shift}" || button === "{lock}") {
      setLayoutName((prev) =>
        prev === "default" ? "shift" : prev === "shift" ? "default" : prev,
      );
    } else if (button === "{numbers}") {
      setLayoutName((prev) => (prev === "numbers" ? "default" : "numbers"));
    } else if (button === "{abc}") {
      setLayoutName("default");
    }
  };

  // Add keydown event listener when comment modal is open
  useEffect(() => {
    if (!isCommentModalOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore if user is already typing in an input field
      if (
        (event.target && (event.target as HTMLElement).tagName === "INPUT") ||
        (event.target as HTMLElement).tagName === "TEXTAREA"
      ) {
        return;
      }

      // Handle special keys
      if (event.key === "Backspace") {
        event.preventDefault();
        setCommentInput((prev) => prev.slice(0, -1));
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        setCommentInput((prev) => prev + "\n");
        return;
      }

      // Handle printable characters
      if (event.key.length === 1) {
        event.preventDefault();
        setCommentInput((prev) => prev + event.key);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isCommentModalOpen]);

  return (
    <Dialog
      open={isCommentModalOpen}
      onClose={handleCloseCommentModal}
      maxWidth="lg"
      fullWidth
    >
      <Box sx={{ px: 3, py: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Comments
        </Typography>

        <TextField
          value={commentInput}
          onChange={(e) => {
            const newValue = e.target.value;
            setCommentInput(newValue);
          }}
          variant="outlined"
          fullWidth
          multiline
          rows={4}
          sx={{
            "& .MuiInputBase-root": {
              height: "150px",
              alignItems: "stretch",
              overflow: "hidden",
              padding: "0 0 0 14px",
            },
            "& .MuiInputBase-inputMultiline": {
              height: "100% !important",
              overflowY: "auto !important",
              boxSizing: "border-box",
              padding: "12px 0",
            },
            "& .MuiInputBase-inputMultiline::-webkit-scrollbar": {
              width: "40px",
            },
            "& .MuiInputBase-inputMultiline::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.grey[100],
              borderLeft: `1px solid ${theme.palette.grey[300]}`,
            },
            "& .MuiInputBase-inputMultiline::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.grey[500],
              border: `7px solid ${theme.palette.grey[100]}`,
              borderRadius: "16px",
            },
            "& .MuiInputBase-inputMultiline::-webkit-scrollbar-button:single-button":
              {
                display: "block",
                height: "44px",
                backgroundColor: theme.palette.grey[200],
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundSize: "28px 28px",
              },
            "& .MuiInputBase-inputMultiline::-webkit-scrollbar-button:single-button:vertical:decrement":
              {
                backgroundImage:
                  'url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2228%22 height=%2228%22 viewBox=%220 0 28 28%22%3E%3Cpath d=%22M5 18l9-9 9 9%22 fill=%22none%22 stroke=%22%234250b5%22 stroke-width=%223.5%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22/%3E%3C/svg%3E")',
              },
            "& .MuiInputBase-inputMultiline::-webkit-scrollbar-button:single-button:vertical:increment":
              {
                backgroundImage:
                  'url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2228%22 height=%2228%22 viewBox=%220 0 28 28%22%3E%3Cpath d=%22M5 10l9 9 9-9%22 fill=%22none%22 stroke=%22%234250b5%22 stroke-width=%223.5%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22/%3E%3C/svg%3E")',
              },
          }}
          slotProps={{
            input: {
              sx: { fontSize: "1.5rem" },
            },
          }}
          disabled={isLoading}
          placeholder="Enter new comments here..."
        />

        <Box mt={1} display="flex" justifyContent="flex-start">
          <Button
            variant="text"
            color="primary"
            startIcon={
              keyboardVisible ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />
            }
            onClick={() => setKeyboardVisible(!keyboardVisible)}
            sx={{ fontSize: "1rem" }}
          >
            {keyboardVisible ? "Hide Keyboard" : "Show Keyboard"}
          </Button>
        </Box>

        <Box mt={1} sx={{ display: keyboardVisible ? "block" : "none" }}>
          <CustomKeyboard
            layoutName={layoutName}
            value={commentInput}
            onChange={(input: string) => setCommentInput(input)}
            onKeyPress={onKeyPress}
          />
        </Box>

        <Box sx={{ ...(styles.buttonsContainer as object), mt: 2 }}>
          <Button
            variant="contained"
            color="inherit"
            onClick={handleCloseCommentModal}
            sx={{
              ...(styles.actionButton as object),
              ...(styles.cancelButton as object),
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              handleSaveComments(commentInput);
              handleCloseCommentModal();
            }}
            sx={styles.actionButton}
            disabled={isLoading}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
